// @flow

import _ from 'lodash';
import type { OauthState, OauthAction } from 'stores/flow-type/oauth.type';

import {
  CONNECT_SHOPIFY_REQUEST,
  CONNECT_SHOPIFY_SUCCESS,
  CONNECT_SHOPIFY_FAILURE,
  UPDATE_TENANT_APP_REQUEST,
  UPDATE_TENANT_APP_SUCCESS,
  UPDATE_TENANT_APP_FAILURE,
  UPDATE_LOCAL_TENANT_APP,
  UPDATE_FEATURE_SETTINGS,
  UPDATE_APP_SCOPES,
  UPDATE_CURRENT_APP_INTEGRATION,
  UPDATE_CURRENT_APP_LANGUAGE_REQUEST,
  UPDATE_CURRENT_APP_LANGUAGE_SUCCESS,
  UPDATE_CURRENT_APP_LANGUAGE_FAILURE,
  UPDATE_CURRENT_APP_VERSION_REQUEST,
  UPDATE_CURRENT_APP_VERSION_SUCCESS,
  UPDATE_CURRENT_APP_VERSION_FAILURE,
  GET_TENANT_APP_GROUPS_REQUEST,
  GET_TENANT_APP_GROUPS_SUCCESS,
  GET_TENANT_APP_GROUPS_FAILURE,
  UNAUTHORIZED_ACCESS,
  UPDATE_WATERMARK_REQUEST,
  UPDATE_WATERMARK_SUCCESS,
  UPDATE_WATERMARK_FAILURE,
  UPDATE_QUERY_PARAMS,
  UPDATE_SMART_STYLING_LIMITATION,
  HANDLE_RE_RANKING_SUCCESS,
  HANDLE_REVIEWS_IO_CONNECT_SUCCESS
} from 'stores/flow-type/oauth.type';
import {
  ENABLE_NEW_ONBOARDING_REQUEST,
  ENABLE_NEW_ONBOARDING_SUCCESS,
  ENABLE_NEW_ONBOARDING_FAILURE,
  SEND_FEEDBACK_AFTER_RATING_5STARS_REQUEST,
  SEND_FEEDBACK_AFTER_RATING_5STARS_SUCCESS,
  SEND_FEEDBACK_AFTER_RATING_5STARS_FAILURE
} from 'stores/flow-type/common.type';
import { ENABLED_MERCHANDISING_V2_RULES_SUCCESS } from 'src/stores/flow-type/merchandising-v2.type';
import { GET_STATUS_MIGRATE_DATA_NEW_SEARCH_SUCCESS } from 'stores/flow-type/common.type';
import {
  UNSUBSCRIBE_ADDON_SUCCESS,
  SUBSCRIBE_ADDON_SUCCESS
} from 'stores/flow-type/subscription-v3.type';
import {
  SET_SHOW_INSTALLATION_STATUS_BANNER_FAILURE,
  SET_SHOW_INSTALLATION_STATUS_BANNER_REQUEST,
  SET_SHOW_INSTALLATION_STATUS_BANNER_SUCCESS,
  DISABLE_SEARCH_ONBOARDING_SUCCESS
} from '../flow-type/dashboard-v3.type';

import {
  AI_POWERED_SEARCH_NAME,
  AI_POWERED_RECOMMENDATION_NAME,
  VISUAL_MERCHANDISING_NAME,
  VARIANT_DISPLAY_NAME,
  SYNC_FREQUENCY_NAME,
  ANALYTICS_ADDON_NAME
} from 'src/constants/subscription.js';

import { UPDATE_AI_SEARCH_STATUS_SUCCESS } from 'stores/flow-type/search-control.type';
import { ADD_OPTION_EMAIL_SUCCESS } from 'stores/flow-type/dashboard/option-email.type';

import { UPDATE_SYNC_PRIORITY_SUCCESS } from 'stores/flow-type/sync.type';
import { checkPremiumStore } from 'src/helpers/subscription-helper';
import { getSubscriptionStatus as getSubscriptionStatusV3 } from 'src/helpers/subscription-v3-helper';
import { getSubscriptionStatus } from 'src/helpers/subscription-helper';
import { checkAndSetSpecialDiscount } from 'src/helpers/dashboard-helper.js';
// import { trackTenantInfoHotJar } from 'src/helpers/hotjar-helper.js';
import { AppType } from 'src/constants/integration';
import { UPDATE_INSTALLATION_STATUS_SUCCESS } from '../flow-type/install.type';

const initialState: OauthState = {
  loading: false,
  connectSuccess: false,
  success: false,
  error: undefined,
  connected: false,
  tenantApp: {},
  host: '',
  authToken: '',
  emailSupport: 'support@boostcommerce.net',
  loadingUpdate: false,
  tenantAppGroups: [],
  loadingChangeLanguage: false,
  loadingChangeAppVersion: false,
  changeAppVersionSuccess: null,
  queryParams: ''
};

const oauthReducers = (state: OauthState = initialState, action: OauthAction) => {
  switch (action.type) {
    case CONNECT_SHOPIFY_REQUEST:
      return { ...state, loading: true, syncLoaded: false };
    case CONNECT_SHOPIFY_SUCCESS: {
      const { data = {}, tenantId, host, isSimulator = false } = action.payload;
      const { tenantApp = {}, authToken } = data;
      const locale = _.get(tenantApp, 'externalInfo.primary_locale', 'en');
      const subscriptionV3 = _.get(tenantApp, 'settings.subscriptionV3.settings.enable', false);
      const subscriptionV4 = _.get(tenantApp, 'settings.subscriptionV4.settings.enable', false);
      const subscriptionV5 = _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false);

      const isPremiumStore = checkPremiumStore(tenantApp);

      const emailSupport = isPremiumStore
        ? 'support-plus@boostcommerce.net'
        : 'support@boostcommerce.net';

      const subscription = _.isEmpty(tenantApp)
        ? {}
        : subscriptionV3 || subscriptionV4
          ? getSubscriptionStatusV3(tenantApp)
          : getSubscriptionStatus(tenantApp);

      if (subscriptionV5) {
        const boltOn = _.get(tenantApp, 'boltOn', {});
        const useBoltOn = boltOn.status === 'active';
        subscription.useBoltOn = useBoltOn;
      }

      checkAndSetSpecialDiscount(tenantId);
      // trackTenantInfoHotJar(tenantApp);

      if (_.isEmpty(tenantApp)) {
        _.set(tenantApp, 'tenantId', tenantId);
      }
      _.set(
        tenantApp,
        'sipParams.syncCollect.prioritizedCollections',
        _.get(tenantApp, 'sipParams.syncCollect.prioritizedCollections', [])
      );

      return {
        ...state,
        loading: false,
        error: undefined,
        connected: true,
        connectSuccess: true,
        tenantApp,
        host,
        subscription,
        authToken,
        emailSupport,
        locale,
        isSimulator
      };
    }

    case CONNECT_SHOPIFY_FAILURE: {
      return {
        ...state,
        loading: false,
        connected: true,
        connectSuccess: false,
        authToken: '',
        tenantApp: {},
        emailSupport: 'support@boostcommerce.net',
        error: action.payload,
        locale: 'en'
      };
    }

    case UPDATE_TENANT_APP_REQUEST:
      return { ...state, loadingUpdate: true };
    case UPDATE_INSTALLATION_STATUS_SUCCESS: {
      const installStatus = action?.payload?.status ?? state.installStatus;
      const { tenantApp } = state;
      tenantApp.installStatus = installStatus;
      return {
        ...state,
        tenantApp,
        loading: false,
        success: true,
        error: undefined
      };
    }
    case UPDATE_TENANT_APP_SUCCESS: {
      const { data: tenantApp = {} } = action.payload;
      const locale = _.get(tenantApp, 'externalInfo.primary_locale', 'en');
      const subscriptionV3 = _.get(tenantApp, 'settings.subscriptionV3.settings.enable', false);

      const isPremiumStore = checkPremiumStore(tenantApp);

      const emailSupport = isPremiumStore
        ? 'support-plus@boostcommerce.net'
        : 'support@boostcommerce.net';

      const subscription = _.isEmpty(tenantApp)
        ? {}
        : subscriptionV3
          ? getSubscriptionStatusV3(tenantApp)
          : getSubscriptionStatus(tenantApp);

      return {
        ...state,
        loadingUpdate: false,
        tenantApp: {
          ...state.tenantApp,
          ...tenantApp
        },
        subscription,
        emailSupport,
        locale
      };
    }

    case UPDATE_TENANT_APP_FAILURE: {
      return {
        ...state,
        loadingUpdate: false
      };
    }

    case UPDATE_LOCAL_TENANT_APP: {
      const { data: newTenantAppData } = action.payload;
      const { tenantApp: oldTenantAppData } = state;
      const tenantApp = _.merge(oldTenantAppData, newTenantAppData);
      return {
        ...state,
        tenantApp
      };
    }

    case UPDATE_APP_SCOPES: {
      const { accessScopes } = action.payload;
      const { tenantApp } = state;

      // Update access scopes
      tenantApp.accessScopes = accessScopes;

      return {
        ...state,
        tenantApp
      };
    }

    case UPDATE_CURRENT_APP_INTEGRATION: {
      const { appType, appName, appKey } = action.payload || {};
      const tenantApp = _.cloneDeep(state.tenantApp);

      // Update access scopes
      tenantApp.currentAppIntegration = { ...tenantApp.currentAppIntegration } || {};
      tenantApp.currentAppIntegration[appType] = appName;

      // handle for review
      if (appType === AppType.reviews) {
        tenantApp.currentAppReviewIntegration = appName;

        // Remove app
        if (!appName) {
          tenantApp.parseParams.stampedAppKey = '';
          tenantApp.parseParams.yotpoAppKey = '';
          tenantApp.parseParams.supportAliReview = false;
        }

        if (appName === 'stamped') {
          tenantApp.parseParams.stampedAppKey = appKey;
          tenantApp.parseParams.yotpoAppKey = '';
          tenantApp.parseParams.supportAliReview = false;
        }

        if (appName === 'yotpo') {
          tenantApp.parseParams.yotpoAppKey = appKey;
          tenantApp.parseParams.stampedAppKey = '';
          tenantApp.parseParams.supportAliReview = false;
        }

        if (appName === 'aliReview') {
          tenantApp.parseParams.supportAliReview = true;
          tenantApp.parseParams.stampedAppKey = '';
          tenantApp.parseParams.yotpoAppKey = '';
        }

        // Force reset old integration of Stamped, Yotpo, AliReview
        if (!['stamped', 'yotpo', 'aliReview'].includes(appName)) {
          tenantApp.parseParams.yotpoAppKey = '';
          tenantApp.parseParams.stampedAppKey = '';
          tenantApp.parseParams.supportAliReview = false;
        }
      }

      return {
        ...state,
        tenantApp
      };
    }

    case HANDLE_REVIEWS_IO_CONNECT_SUCCESS: {
      const { connected } = action.payload.data || {};
      const tenantApp = _.cloneDeep(state.tenantApp);
      if (!connected) {
        _.set(tenantApp, 'currentAppIntegration.extraFeatures.reviewsio.connected', false);
        _.set(tenantApp, 'currentAppIntegration.extraFeatures.reviewsio.reRanking', false);
        _.set(tenantApp, 'currentAppIntegration.reviews', '');
      } else {
        _.set(tenantApp, 'currentAppIntegration.extraFeatures.reviewsio.connected', true);
      }

      return {
        ...state,
        tenantApp
      };
    }

    case HANDLE_RE_RANKING_SUCCESS: {
      const { reRanking } = action.payload.data || {};
      const { tenantApp } = state;
      _.set(tenantApp, 'currentAppIntegration.extraFeatures.reviewsio.reRanking', reRanking);
      return {
        ...state,
        tenantApp
      };
    }

    case UPDATE_CURRENT_APP_LANGUAGE_REQUEST:
      return { ...state, loadingChangeLanguage: true };

    case UPDATE_CURRENT_APP_LANGUAGE_SUCCESS: {
      const { data } = action.payload;
      const { appLanguage } = data;
      const { tenantApp } = state;

      tenantApp.appLanguage = appLanguage;

      return {
        ...state,
        loadingChangeLanguage: false,
        tenantApp
      };
    }

    case UPDATE_CURRENT_APP_LANGUAGE_FAILURE:
      return { ...state, loadingChangeLanguage: false };

    case UPDATE_CURRENT_APP_VERSION_REQUEST:
      return { ...state, loadingChangeAppVersion: true, changeAppVersionSuccess: null };

    case UPDATE_CURRENT_APP_VERSION_SUCCESS: {
      const { data } = action.payload;
      const { appVersion, settings } = data;
      const { tenantApp } = state;

      tenantApp.appVersion = appVersion;
      tenantApp.settings = settings;

      return {
        ...state,
        loadingChangeAppVersion: false,
        changeAppVersionSuccess: true,
        tenantApp
      };
    }

    case UPDATE_CURRENT_APP_VERSION_FAILURE:
      return { ...state, loadingChangeAppVersion: false, changeAppVersionSuccess: false };

    case UNAUTHORIZED_ACCESS: {
      return {
        ...state,
        connectSuccess: false,
        authToken: '',
        tenantApp: {}
      };
    }
    case GET_STATUS_MIGRATE_DATA_NEW_SEARCH_SUCCESS: {
      const { data } = action.payload;
      const tenantApp = _.get(data, 'tenantApp', {});
      const status = _.get(data, 'status', null);
      if (status === 'done') {
        return {
          ...state,
          tenantApp: _.merge(state.tenantApp, tenantApp)
        };
      }

      return {
        ...state
      };
    }
    case DISABLE_SEARCH_ONBOARDING_SUCCESS: {
      const { data } = action.payload;
      const enable = _.get(data, 'settings.enable', false);

      const showInstallationSuccessBanner = _.get(
        data,
        'settings.showInstallationSuccessBanner',
        false
      );
      const showInstallationThemeFailBanner = _.get(
        data,
        'settings.showInstallationThemeFailBanner',
        false
      );
      const showStandardSearchOnboarding = _.get(
        data,
        'settings.showStandardSearchOnboarding',
        false
      );

      const newSettings = {
        enable,
        showInstallationSuccessBanner,
        showInstallationThemeFailBanner,
        showStandardSearchOnboarding
      };

      _.set(state.tenantApp, 'settings.onboardingV3.settings', newSettings);

      return {
        ...state,
        tenantApp: state.tenantApp
      };
    }
    case SET_SHOW_INSTALLATION_STATUS_BANNER_REQUEST:
      return { ...state, loading: true };
    case SET_SHOW_INSTALLATION_STATUS_BANNER_SUCCESS: {
      const { data } = action.payload;
      const setting = _.get(state.tenantApp, 'settings', {});
      const value = _.get(data, 'settings.enable', false);
      const status = _.get(data, 'settings.status', 'active');
      const showInstallationSuccessBanner = _.get(
        data,
        'settings.showInstallationSuccessBanner',
        false
      );
      const showInstallationThemeFailBanner = _.get(
        data,
        'settings.showInstallationThemeFailBanner',
        false
      );
      const showStandardSearchOnboarding = _.get(
        data,
        'settings.showStandardSearchOnboarding',
        false
      );
      const newSettings = {
        ...setting,
        onboardingV3: {
          settings: {
            enable: value,
            showInstallationSuccessBanner,
            showInstallationThemeFailBanner,
            showStandardSearchOnboarding
          },
          status
        }
      };
      return {
        ...state,
        loading: false,
        success: true,
        onboarding: data,
        tenantApp: {
          ...state.tenantApp,
          settings: newSettings
        }
      };
    }
    case SET_SHOW_INSTALLATION_STATUS_BANNER_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    case ENABLED_MERCHANDISING_V2_RULES_SUCCESS: {
      const { data } = action.payload;

      const settings = _.get(data, 'settings', {});

      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          settings
        }
      };
    }
    case ENABLE_NEW_ONBOARDING_REQUEST:
      return { ...state, loading: true };
    case ENABLE_NEW_ONBOARDING_SUCCESS: {
      const { data } = action.payload;
      const setting = _.get(state.tenantApp, 'settings', {});
      const value = _.get(data, 'settings.enable', false);
      const status = _.get(data, 'settings.status', 'active');
      const showInstallationSuccessBanner = _.get(
        data,
        'settings.showInstallationSuccessBanner',
        false
      );
      const showInstallationThemeFailBanner = _.get(
        data,
        'settings.showInstallationThemeFailBanner',
        false
      );
      const showStandardSearchOnboarding = _.get(
        data,
        'settings.showStandardSearchOnboarding',
        false
      );
      const newSettings = {
        ...setting,
        onboardingV3: {
          settings: {
            enable: value,
            showInstallationSuccessBanner,
            showInstallationThemeFailBanner,
            showStandardSearchOnboarding
          },
          status
        }
      };
      return {
        ...state,
        loading: false,
        success: true,
        onboarding: data,
        tenantApp: {
          ...state.tenantApp,
          settings: newSettings
        }
      };
    }
    case ENABLE_NEW_ONBOARDING_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    case UNSUBSCRIBE_ADDON_SUCCESS: {
      const { data } = action.payload;
      const dataSettings = _.get(data, 'settings', {});
      const settings = _.isEmpty(dataSettings)
        ? _.get(state.tenantApp, 'settings', {})
        : dataSettings;

      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          settings
        }
      };
    }
    // Update feature app settings
    case UPDATE_FEATURE_SETTINGS: {
      const { data, featureName } = action.payload;
      const settings = _.get(state.tenantApp, 'settings', {});
      let newSettings = { ...settings };
      const currentSettings = _.get(newSettings, `${featureName}.settings`, {});
      _.set(newSettings, `${featureName}.settings`, {
        ...currentSettings,
        ...data
      });

      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          settings: newSettings
        }
      };
    }
    case SUBSCRIBE_ADDON_SUCCESS: {
      const { addonName } = action.payload;
      const setting = _.get(state.tenantApp, 'settings', {});
      let newSettings = { ...setting };
      switch (addonName) {
        case AI_POWERED_SEARCH_NAME: {
          _.set(newSettings, 'instantSearch.settings.AI-synonyms', true);
          if (_.has(newSettings, 'instantSearch.settings.AI-search-features-status')) {
            // update tenant app toggle status ai search features
            _.set(newSettings, 'instantSearch.settings.AI-search-features-status', true);
          }
          break;
        }
        case AI_POWERED_RECOMMENDATION_NAME: {
          _.set(newSettings, 'recommendation.settings.frequently-bought-together', true);
          _.set(newSettings, 'recommendation.settings.related-items', true);
          _.set(newSettings, 'recommendation.settings.recently-purchased', true);
          _.set(newSettings, 'postPurchase.settings.enable', true);
          break;
        }
        case VISUAL_MERCHANDISING_NAME: {
          _.set(newSettings, 'visualMerchandising.settings.collection-page', true);
          break;
        }
        case VARIANT_DISPLAY_NAME: {
          _.set(newSettings, 'variantsDisplay.settings.variant-display', true);
          break;
        }
        case SYNC_FREQUENCY_NAME: {
          _.set(newSettings, 'syncFrequency.settings.enable', true);
          break;
        }
        default: {
          if (_.includes(addonName, ANALYTICS_ADDON_NAME)) {
            _.set(newSettings, `analyticV2.settings.${addonName}`, true);
          }
        }
      }
      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          settings: newSettings
        }
      };
    }

    case UPDATE_AI_SEARCH_STATUS_SUCCESS: {
      const { enabled } = action.payload.data;
      const setting = _.get(state.tenantApp, 'settings', {});
      let newSettings = { ...setting };
      const currentSettings = _.get(newSettings, `instantSearch.settings`, {});
      _.set(newSettings, `instantSearch.settings`, {
        ...currentSettings,
        'AI-search-features-status': enabled
      });
      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          settings: newSettings
        }
      };
    }

    case SEND_FEEDBACK_AFTER_RATING_5STARS_REQUEST:
      return { ...state };
    case SEND_FEEDBACK_AFTER_RATING_5STARS_SUCCESS: {
      const feedbackAfterRating5Stars = _.get(state, 'tenantApp.feedbackAfterRating5Stars', {});
      _.assign(feedbackAfterRating5Stars, {
        isNotShowOptinNotifyForever: true,
        isNotShowPopupForever: true
      });
      return {
        ...state,
        tenantApp: {
          ...state.tenantApp,
          feedbackAfterRating5Stars
        }
      };
    }
    case SEND_FEEDBACK_AFTER_RATING_5STARS_FAILURE:
      return { ...state };
    case GET_TENANT_APP_GROUPS_REQUEST:
      return { ...state, tenantAppGroups: [] };

    case GET_TENANT_APP_GROUPS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        tenantAppGroups: data
      };
    }

    case GET_TENANT_APP_GROUPS_FAILURE:
      return { ...state, tenantAppGroups: [] };

    case UPDATE_WATERMARK_REQUEST:
      return { ...state };

    case UPDATE_WATERMARK_SUCCESS: {
      const { data } = action.payload;
      _.set(state.tenantApp, 'settings.appPreferences.settings.watermark', data.watermark);
      return {
        ...state,
        tenantApp: state.tenantApp
      };
    }
    case UPDATE_WATERMARK_FAILURE:
      return { ...state };
    case UPDATE_SYNC_PRIORITY_SUCCESS: {
      const prioritizedCollections = _.get(action.payload, 'collections', []);
      const _state = {
        ...state
      };
      _.set(
        _state,
        'tenantApp.sipParams.syncCollect.prioritizedCollections',
        prioritizedCollections
      );
      return _state;
    }
    case ADD_OPTION_EMAIL_SUCCESS: {
      // when update here, dirty of form will be set to false
      const optEmail = _.get(action.payload, 'body.optEmail');
      const _state = {
        ...state
      };
      _.set(_state, 'tenantApp.optEmail', optEmail);
      return _state;
    }
    case UPDATE_QUERY_PARAMS: {
      return { ...state, queryParams: action.payload };
    }

    case UPDATE_SMART_STYLING_LIMITATION: {
      const settings = action.payload;
      const newTenantApp = _.cloneDeep(state.tenantApp);
      _.set(newTenantApp, 'settings.smartStyling.settings', settings);

      return { ...state, tenantApp: newTenantApp };
    }

    default:
      return state;
  }
};
export default oauthReducers;
